import React, { useEffect, useRef, useState } from 'react';
import { MessageCircle, X, Send, Sparkles, Loader2 } from 'lucide-react';
import { API } from '../lib/api';
import axios from 'axios';

const STORAGE_KEY = 'pi_chat_session_id';

const suggested = [
  'What is CloudRebuild?',
  'How does cross-cloud restore work?',
  'Tell me about Zaatly',
  'Book a demo'
];

const getSessionId = () => {
  let sid = localStorage.getItem(STORAGE_KEY);
  if (!sid) {
    sid = `visitor-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(STORAGE_KEY, sid);
  }
  return sid;
};

const ChatBubble = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // {role, content}
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [sessionId] = useState(getSessionId);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  // hydrate history once
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/ai/chat/${sessionId}`).then((res) => {
      if (!cancelled && res.data?.messages?.length) {
        setMessages(res.data.messages.map((m) => ({ role: m.role, content: m.content })));
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [sessionId]);

  // autoscroll
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, busy, open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 200);
  }, [open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: msg }, { role: 'assistant', content: '' }]);
    setBusy(true);

    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, message: msg }),
        credentials: 'include',
      });
      if (!res.ok || !res.body) throw new Error('bad response');

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split('\n\n');
        buffer = chunks.pop() || '';
        for (const chunk of chunks) {
          const line = chunk.trim();
          if (!line.startsWith('data:')) continue;
          const jsonStr = line.slice(5).trim();
          try {
            const parsed = JSON.parse(jsonStr);
            if (parsed.delta) {
              setMessages((prev) => {
                const next = [...prev];
                next[next.length - 1] = {
                  role: 'assistant',
                  content: next[next.length - 1].content + parsed.delta,
                };
                return next;
              });
            } else if (parsed.error) {
              setMessages((prev) => {
                const next = [...prev];
                next[next.length - 1] = { role: 'assistant', content: parsed.error };
                return next;
              });
            }
          } catch { /* ignore */ }
        }
      }
    } catch {
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = {
          role: 'assistant',
          content: 'Sorry — I had trouble connecting. Please try again in a moment.',
        };
        return next;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* Floating toggle */}
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="chat-bubble-toggle"
        aria-label={open ? 'Close chat' : 'Open chat'}
        className="fixed z-[60] bottom-6 right-6 h-14 w-14 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 text-[#0b1220] shadow-2xl shadow-amber-500/30 flex items-center justify-center hover:scale-105 transition-transform"
      >
        {open ? <X size={22} strokeWidth={2.4} /> : <MessageCircle size={22} strokeWidth={2.2} />}
      </button>

      {/* Panel */}
      {open && (
        <div
          data-testid="chat-bubble-panel"
          className="fixed z-[60] bottom-24 right-6 w-[380px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-8rem)] flex flex-col rounded-3xl border border-white/10 bg-[#05080f]/95 backdrop-blur-xl shadow-2xl overflow-hidden"
        >
          {/* header */}
          <div className="p-4 border-b border-white/5 bg-gradient-to-br from-amber-500/10 to-transparent flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-[#0b1220]">
              <Sparkles size={16} strokeWidth={2.4} />
            </div>
            <div className="flex-1">
              <div className="text-white text-sm font-semibold">Pratham AI</div>
              <div className="text-slate-400 text-[11px]">Ask about our cloud, AI, or CloudRebuild</div>
            </div>
          </div>

          {/* messages */}
          <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3 text-sm">
            {messages.length === 0 && (
              <div className="text-slate-400 leading-relaxed">
                Hey — I&apos;m Pratham AI. I can answer questions about our services, CloudRebuild
                (multi-cloud DR), and Zaatly. Try one:
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 whitespace-pre-wrap leading-relaxed ${
                    m.role === 'user'
                      ? 'bg-amber-500/20 text-amber-100 border border-amber-500/25 rounded-br-sm'
                      : 'bg-white/[0.04] text-slate-200 border border-white/10 rounded-bl-sm'
                  }`}
                >
                  {m.content || (busy && i === messages.length - 1 ? (
                    <span className="inline-flex items-center gap-2 text-slate-400 text-xs">
                      <Loader2 size={12} className="animate-spin" /> thinking…
                    </span>
                  ) : '')}
                </div>
              </div>
            ))}
            {messages.length === 0 && (
              <div className="flex flex-wrap gap-2 pt-2">
                {suggested.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    data-testid={`chat-suggest-${s.split(' ')[0].toLowerCase()}`}
                    className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-slate-300 hover:border-amber-500/30 hover:text-amber-200 transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* composer */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="p-3 border-t border-white/5 flex items-center gap-2"
          >
            <input
              ref={inputRef}
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your question…"
              disabled={busy}
              className="flex-1 h-10 rounded-full bg-white/5 border border-white/10 px-4 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-amber-500/40"
            />
            <button
              type="submit"
              data-testid="chat-send"
              disabled={busy || !input.trim()}
              className="h-10 w-10 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 text-[#0b1220] flex items-center justify-center disabled:opacity-50 hover:scale-105 transition-transform"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} strokeWidth={2.4} />}
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default ChatBubble;
